import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CreditCard, Download, FileText, Loader2, Lock, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { fetchDownload, claimFreeDownload, initDownloadPayment, verifyDownloadPayment, apiErr } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { breadcrumbJsonLd } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "mt-1.5 h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-gold-300/50 focus:outline-none";

export default function DownloadDetail() {
    const { slug } = useParams();
    const [d, setD] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState({ full_name: "", email: "", mobile: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null); // {download_url, order_code?}

    useEffect(() => {
        setD(null); setDone(null);
        fetchDownload(slug).then(setD).catch(() => setNotFound(true));
        loadRazorpay();
    }, [slug]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const valid = () => {
        if (!form.full_name || !form.email || !form.mobile) { toast.error("Please fill your name, email and mobile."); return false; }
        return true;
    };

    const getFree = async () => {
        if (!valid()) return;
        setBusy(true);
        try {
            const r = await claimFreeDownload(slug, form);
            setDone({ download_url: r.download_url });
            toast.success("Your download is ready!");
        } catch (e) { toast.error(apiErr(e, "Could not prepare your download.")); }
        finally { setBusy(false); }
    };

    const buyNow = async () => {
        if (!valid()) return;
        setBusy(true);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error("razorpay");
            const order = await initDownloadPayment(slug, form);
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "Abhijit JK",
                description: order.title,
                order_id: order.razorpay_order_id,
                prefill: { name: form.full_name, email: form.email, contact: form.mobile },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        const v = await verifyDownloadPayment({
                            order_id: order.order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        setDone({ download_url: v.download_url, order_code: v.order_code });
                        toast.success("Payment successful! Your download is ready.");
                    } catch { toast.error("Payment verification failed. Contact support."); }
                    finally { setBusy(false); }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(false); });
            rzp.open();
        } catch (e) {
            toast.error(apiErr(e, "Could not start payment. Please try again."));
            setBusy(false);
        }
    };

    if (notFound) return (
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
            <p className="font-display text-2xl">Download not found.</p>
            <Link to="/downloads" className="gold-button mt-6 inline-block">Browse all downloads</Link>
        </div>
    );
    if (!d) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-gold-300" size={28} /></div>;

    const isFree = d.type === "free";
    return (
        <div data-testid="download-detail-page" className="relative">
            <Seo
                title={d.seo_title || `${d.title} — ${isFree ? "Free" : "Premium"} Download | Abhijit JK`}
                description={d.meta_description || d.short_description || `Download ${d.title} by Abhijit JK.`}
                path={`/downloads/${d.slug}`}
                jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Downloads", path: "/downloads" }, { name: d.title, path: `/downloads/${d.slug}` }])]}
            />
            <section className="mx-auto max-w-7xl px-6 pt-12 pb-24 lg:px-10">
                <Link to="/downloads" data-testid="back-to-downloads" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white"><ArrowLeft size={15} /> All downloads</Link>
                <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_420px]">
                    {/* LEFT */}
                    <div>
                        <Reveal>
                            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider">
                                {d.category && <span className="rounded-full bg-white/5 px-3 py-1 text-white/55">{d.category}</span>}
                                {d.file_type && <span className="rounded-full bg-white/5 px-3 py-1 text-white/55">{d.file_type.toUpperCase()}{d.file_size ? ` · ${d.file_size}` : ""}</span>}
                                <span className={`rounded-full px-3 py-1 ${isFree ? "bg-emerald-400/15 text-emerald-300" : "bg-gold-300/15 text-gold-300"}`}>{isFree ? "Free Download" : "Premium"}</span>
                            </div>
                            <h1 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{d.title}</h1>
                            {d.short_description && <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/65">{d.short_description}</p>}
                        </Reveal>
                        {d.image && (
                            <Reveal delay={0.1}>
                                <div className="mt-8 overflow-hidden rounded-lg">
                                    <img src={`${BACKEND}${d.image}`} alt={d.title} className="w-full object-cover" />
                                </div>
                            </Reveal>
                        )}
                        {d.description && (
                            <Reveal delay={0.15}>
                                <div className="mt-8 space-y-4">
                                    {d.description.split(/\n\s*\n/).map((p, i) => <p key={i} className="text-base leading-relaxed text-white/70">{p}</p>)}
                                </div>
                            </Reveal>
                        )}
                        {(d.features || []).length > 0 && (
                            <Reveal delay={0.2}>
                                <div className="mt-10">
                                    <h2 className="font-display text-lg md:text-lg">What's <span className="gold-text">inside</span></h2>
                                    <ul className="mt-4 space-y-3">
                                        {d.features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm text-white/75"><Check size={15} className="mt-0.5 flex-none text-gold-300" /> {f}</li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                    </div>

                    {/* RIGHT — action card */}
                    <div className="lg:sticky lg:top-28 lg:self-start">
                        <Reveal delay={0.1}>
                            <div className="glass glow-border rounded-lg p-7" data-testid="download-action-card">
                                {done ? (
                                    <div className="text-center" data-testid="download-success">
                                        <Download className="mx-auto text-gold-300" size={32} />
                                        <h3 className="mt-4 font-display text-xl">{isFree ? "Ready to download!" : "Purchase successful!"}</h3>
                                        {done.order_code && <p className="mt-2 text-xs text-white/45">Order: {done.order_code}</p>}
                                        <p className="mt-3 text-sm leading-relaxed text-white/60">
                                            {isFree ? "Your file is ready — click below to download." : "Your secure download link is ready. You can also retrieve it anytime from the Downloads page using your email & mobile."}
                                        </p>
                                        <a href={`${BACKEND}${done.download_url}`} data-testid="download-file-btn"
                                            className="gold-button mt-6 inline-flex w-full items-center justify-center gap-2">
                                            <Download size={15} /> Download {d.file_type ? d.file_type.toUpperCase() : "File"}
                                        </a>
                                    </div>
                                ) : (
                                    <>
                                        {isFree ? (
                                            <p className="font-display text-2xl text-emerald-300">Free</p>
                                        ) : (
                                            <p className="font-display text-3xl gold-text">₹{(d.price || 0).toLocaleString("en-IN")}
                                                {d.strike_price && <span className="ml-3 text-lg text-white/35 line-through">₹{d.strike_price.toLocaleString("en-IN")}</span>}
                                            </p>
                                        )}
                                        <p className="mt-2 text-xs text-white/45">{isFree ? "Enter your details to get instant access." : "One-time payment · Instant secure download after payment."}</p>
                                        <div className="mt-6 space-y-4">
                                            <div><label className="text-xs uppercase tracking-wider text-white/50">Full name *</label>
                                                <input data-testid="dl-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" /></div>
                                            <div><label className="text-xs uppercase tracking-wider text-white/50">Email *</label>
                                                <input data-testid="dl-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" /></div>
                                            <div><label className="text-xs uppercase tracking-wider text-white/50">Mobile *</label>
                                                <input data-testid="dl-mobile" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" /></div>
                                        </div>
                                        {isFree ? (
                                            <button data-testid="dl-free-btn" onClick={getFree} disabled={busy} className="gold-button mt-6 inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
                                                {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download Free
                                            </button>
                                        ) : (
                                            <button data-testid="dl-pay-btn" onClick={buyNow} disabled={busy} className="gold-button mt-6 inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
                                                {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Pay ₹{(d.price || 0).toLocaleString("en-IN")} & Download
                                            </button>
                                        )}
                                        <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-white/40">
                                            {isFree ? <FileText size={12} className="text-gold-300" /> : <Lock size={12} className="text-gold-300" />}
                                            {isFree ? "Instant delivery — no payment needed" : "Secure Razorpay payment · Private download link"}
                                        </p>
                                        {!isFree && <p className="mt-2 flex items-center justify-center gap-2 text-[11px] text-white/40"><ShieldCheck size={12} className="text-gold-300" /> Lost your link? Retrieve anytime with email + mobile</p>}
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
